import { BackButton } from "@/components/BackButton";
import { FrameCard } from "@/components/FrameCard";
import { PageTransition } from "@/components/PageTransition";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@photo-booth/ui/carousel";
import { Spinner } from "@photo-booth/ui/spinner";
import { useGetFrames } from "@/hooks/api/useGetFrames";
import { AppError } from "@/lib/errorUtils";
import type { FrameModel } from "@/lib/model/Frame";
import { useFrameStore } from "@/lib/store/frameStore";
import { useNavigate, useSearchParams } from "react-router";
import { useSEO } from "@/lib/seo/useSEO";

export const FramesPage: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetFrames();
  const { setFrame } = useFrameStore();
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type") || "monthly";

  const handleFrameClick = (frame: FrameModel) => {
    setFrame(frame);
    navigate("/frame-detail");
  };

  if (error) {
    throw new AppError(error.message ?? "Failed to get frames", "processing");
  }

  const frameTypeLabel = type === "monthly" ? "Monthly" : type.charAt(0).toUpperCase() + type.slice(1);
  
  useSEO({
    title: `${frameTypeLabel} Frames - Choose Your Style`,
    description: `Browse our collection of ${type} photo frames. Select the perfect frame style for your photo booth experience with KeptScene.`,
    keywords: `${type} frames, photo frames, photo booth frames, KeptScene frames`,
  });
  
  return (
    <PageTransition className="min-h-screen overflow-y-hidden">
      <BackButton prevUrl="/photo-booth" />
      <div className="px-8 py-6">
        <div className="bg-[url('/tape.png')] bg-cover bg-center w-full text-center -rotate-6">
          <h1 className="text-[40px] font-family-reenie text-vintage-black mb-8">{`${type.toUpperCase()} FRAMES`}</h1>
        </div>
        <Carousel className="mx-[20px] mt-8">
          {isLoading ? (
            <div className="w-full h-[70vh] flex items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <CarouselContent className="grid grid-flow-row grid-rows-3 grid-cols-2 gap-4">
              {data?.map((frame, index) => (
                <CarouselItem
                  key={frame.childFrameId}
                  className="flex items-center justify-center"
                >
                  <FrameCard
                    frame={frame}
                    priority={index < 4}
                    onClick={() => handleFrameClick(frame)}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
          )}
          <CarouselPrevious className="bg-transparent border-none" />
          <CarouselNext className="bg-transparent border-none" />
        </Carousel>
        <p className="text-center font-family-reenie opacity-80 text-vintage-red mt-8">
          * Result may vary depending on light and position of the photo. Display image is for reference only.
        </p>
      </div>
    </PageTransition>
  );
};
